import { Box3, Vector3 } from 'three';

import { SubtreeTile, TileCoordinates, Tiles3tzSubtree } from './types';

export type Tiles3tzTile = TileCoordinates & {
  key: string;
  /** In the block's frame: x east, y north, z up, metres from the block's origin. */
  box: Box3;
  geometricError: number;
  content?: { byteOffset: number; byteLength: number };
  /** Its available children loaded so far. */
  children: Tiles3tzTile[];
  /** The subtrees rooted at its children, not loaded yet. Only a subtree's bottom tiles have any. */
  childSubtrees: TileCoordinates[];
};

export function tileKey({ level, x, y }: TileCoordinates) {
  return `${level}-${x}-${y}`;
}

function parentKey({ level, x, y }: TileCoordinates) {
  return tileKey({ level: level - 1, x: x >> 1, y: y >> 1 });
}

/** The axis-aligned box of a 3D Tiles box: a centre and three half-axes. */
function toBox3(box: number[]) {
  const [cx, cy, cz] = box;
  const extent = new Vector3(
    Math.abs(box[3]) + Math.abs(box[6]) + Math.abs(box[9]),
    Math.abs(box[4]) + Math.abs(box[7]) + Math.abs(box[10]),
    Math.abs(box[5]) + Math.abs(box[8]) + Math.abs(box[11]),
  );
  const center = new Vector3(cx, cy, cz);
  return new Box3(center.clone().sub(extent), center.add(extent));
}

/**
 * The quadtree of a 3tz's available tiles, which grows as its subtrees load. A tile at the bottom of
 * a loaded subtree knows its children only once the subtrees below it load.
 */
export class Tiles3tzTree {
  root?: Tiles3tzTile;
  private tiles = new Map<string, Tiles3tzTile>();

  get(coordinates: TileCoordinates) {
    return this.tiles.get(tileKey(coordinates));
  }

  /** Adds the tiles of the subtree rooted at the tile, which its parent then no longer waits for. */
  addSubtree(root: TileCoordinates, subtree: Tiles3tzSubtree) {
    const parent = root.level > 0 ? this.tiles.get(parentKey(root)) : undefined;
    if (parent) {
      parent.childSubtrees = parent.childSubtrees.filter(
        (childSubtree) => tileKey(childSubtree) !== tileKey(root),
      );
    }

    for (const tile of subtree.tiles) {
      this.addTile(tile);
    }
    for (const childSubtree of subtree.childSubtrees) {
      this.tiles.get(parentKey(childSubtree))?.childSubtrees.push(childSubtree);
    }
  }

  private addTile({ level, x, y, box, geometricError, content }: SubtreeTile) {
    const tile: Tiles3tzTile = {
      level,
      x,
      y,
      key: tileKey({ level, x, y }),
      box: toBox3(box),
      geometricError,
      content,
      children: [],
      childSubtrees: [],
    };
    this.tiles.set(tile.key, tile);

    if (level === 0) {
      this.root = tile;
      return;
    }
    // Tiles come level by level, so the parent is there, in this subtree or the one above
    this.tiles.get(parentKey(tile))?.children.push(tile);
  }
}
